<template>
  <!-- 使用自定义弹窗替代Vant Dialog -->
  <div
    v-if="showDialog"
    class="welcome-overlay"
    @click.self="handleOverlayClick"
  >
    <div class="welcome-modal">
      <div class="welcome-header">
        <h2>欢迎使用睡眠问诊小助手</h2>
      </div>

      <div class="welcome-content">
        <div class="welcome-icon">
          <svg
            width="64"
            height="64"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <path
              d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"
            />
            <path d="M8 9h8" />
            <path d="M8 13h6" />
          </svg>
        </div>

        <div class="welcome-text">
          <p class="welcome-title">欢迎参与AI智能问卷调研</p>
          <p class="welcome-description">
            我是您的AI助手，将通过对话的方式与您进行问卷调研。
            您可以使用文字或语音与我交流，整个过程轻松自然。
          </p>

          <div class="welcome-features">
            <div class="feature-item">
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path
                  d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"
                />
              </svg>
              <span>支持文字和语音交流</span>
            </div>

            <div class="feature-item">
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path
                  d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"
                />
              </svg>
              <span>自然对话式问卷体验</span>
            </div>

            <div class="feature-item">
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path
                  d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"
                />
              </svg>
              <span>智能理解您的回答</span>
            </div>
          </div>
        </div>
      </div>

      <div class="welcome-footer">
        <button class="confirm-button" @click="handleConfirm">开始对话</button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch } from "vue";

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["confirm", "update:show"]);

const showDialog = ref(false);

// 处理确认按钮点击
const handleConfirm = () => {
  showDialog.value = false;
  emit("update:show", false);
  emit("confirm");
};

// 监听 props 变化
watch(
  () => props.show,
  (newValue) => {
    showDialog.value = newValue;
  },
  { immediate: true }
);

// 处理点击遮罩层
const handleOverlayClick = () => {
  // 不允许点击遮罩层关闭弹窗
  // 用户必须点击"开始对话"按钮
};

// 监听外部 show 变化
const updateShow = (value) => {
  showDialog.value = value;
};

// 暴露方法
defineExpose({
  updateShow,
});
</script>

<style lang="scss" scoped>
.welcome-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
  box-sizing: border-box;
}

.welcome-modal {
  background: white;
  border-radius: 12px;
  max-width: 400px;
  width: 100%;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  animation: modalSlideIn 0.3s ease-out;
}

@keyframes modalSlideIn {
  from {
    opacity: 0;
    transform: translateY(-20px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.welcome-header {
  padding: 24px 24px 16px;
  border-bottom: 1px solid #f0f0f0;

  h2 {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    color: #333;
    text-align: center;
  }
}

.welcome-content {
  padding: 24px;
  text-align: center;
}

.welcome-icon {
  margin-bottom: 20px;

  svg {
    color: #667eea;
    width: 64px;
    height: 64px;
  }
}

.welcome-text {
  .welcome-title {
    font-size: 16px;
    font-weight: 600;
    color: #333;
    margin-bottom: 12px;
    line-height: 1.4;
  }

  .welcome-description {
    font-size: 14px;
    color: #666;
    line-height: 1.6;
    margin-bottom: 20px;
  }
}

.welcome-features {
  text-align: left;

  .feature-item {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;
    font-size: 14px;
    color: #555;

    svg {
      color: #52c41a;
      flex-shrink: 0;
    }

    &:last-child {
      margin-bottom: 0;
    }
  }
}

.welcome-footer {
  padding: 16px 24px 24px;
  border-top: 1px solid #f0f0f0;
  display: flex;
  flex-direction: column;
  gap: 12px;

  .confirm-button {
    width: 100%;
    height: 44px;
    border: none;
    border-radius: 22px;
    font-size: 16px;
    font-weight: 500;
    color: white;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    cursor: pointer;
    transition: all 0.3s ease;

    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
    }

    &:active {
      transform: translateY(0);
    }
  }
}

// 移动端适配
@media (max-width: 480px) {
  .welcome-overlay {
    padding: 15px;
  }

  .welcome-header {
    padding: 20px 20px 12px;

    h2 {
      font-size: 16px;
    }
  }

  .welcome-content {
    padding: 20px;
  }

  .welcome-footer {
    padding: 12px 20px 20px;

    .confirm-button {
      height: 40px;
      font-size: 15px;
    }
  }

  .welcome-icon svg {
    width: 56px;
    height: 56px;
  }

  .welcome-text {
    .welcome-title {
      font-size: 15px;
    }

    .welcome-description {
      font-size: 13px;
    }
  }

  .welcome-features .feature-item {
    font-size: 13px;
  }
}
</style>